import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingCart, BadgeCheck, Ticket } from 'lucide-react';
import { fmtRM } from '../data/mockData';
import { useApp } from '../context/AppContext';
import { EmptyState } from '../components/commerce/EmptyState';
import { toast } from 'sonner';

export default function CartPage() {
  const { cart, updateCartQty, toggleCartItem, toggleCartMerchant, removeCartItem, customProducts, merchantById, vouchers } = useApp();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState(localStorage.getItem('kelate_voucher') || '');

  const groups = {};
  cart.forEach((c) => {
    const p = customProducts.find((pp) => pp.id === c.productId);
    if (!p) return;
    (groups[p.merchantId] = groups[p.merchantId] || []).push({ ...c, product: p });
  });
  const merchantIds = Object.keys(groups);
  const selectedItems = cart.filter((c) => c.selected);
  const subtotal = selectedItems.reduce((sum, c) => {
    const p = customProducts.find((pp) => pp.id === c.productId);
    return sum + (p ? p.price * c.qty : 0);
  }, 0);
  const v = vouchers.find((x) => x.active && x.code === voucher);
  const vDesc = v ? `${v.type === 'PERCENT' ? `${v.value}% off` : `${fmtRM(v.value)} off`}${v.minSpend > 0 ? ` · min spend ${fmtRM(v.minSpend)}` : ''}${v.scope === 'MERCHANT' ? ` · ${v.merchantName} only` : ' · all stores'}` : '';

  if (cart.length === 0) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-10">
        <EmptyState icon={ShoppingCart} title="Your cart is empty" description="Discover local products and support Kelantan merchants." actionLabel="Browse Marketplace" actionTo="/marketplace" testId="cart-empty" />
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="mx-auto max-w-[1280px] px-4 py-8 pb-32 md:pb-8">
      <h1 className="font-display text-2xl font-semibold text-kelate-darkwood sm:text-3xl">Cart</h1>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          {merchantIds.map((mid) => {
            const merchant = merchantById(mid);
            if (!merchant) return null;
            const items = groups[mid];
            const allSelected = items.every((i) => i.selected);
            return (
              <div key={mid} className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)]" data-testid={`cart-merchant-${merchant.slug}`}>
                <label className="flex items-center gap-2 border-b border-kelate-line px-4 py-3">
                  <input type="checkbox" checked={allSelected} onChange={(e) => toggleCartMerchant(mid, e.target.checked)} className="accent-kelate-darkwood" data-testid={`select-merchant-${merchant.slug}`} />
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-kelate-ink">{merchant.name} {merchant.verified && <BadgeCheck size={13} className="text-kelate-sage" />}</span>
                </label>
                {items.map((item) => (
                  <div key={`${item.productId}|${item.variant}`} className="flex items-center gap-3 border-b border-kelate-line/60 px-4 py-3 last:border-0">
                    <input type="checkbox" checked={item.selected} onChange={() => toggleCartItem(item.productId, item.variant)} className="accent-kelate-darkwood" data-testid={`select-item-${item.product.slug}`} />
                    <img src={item.product.image} alt="" className="h-16 w-16 rounded-sm border border-kelate-line object-cover" />
                    <div className="min-w-0 flex-1">
                      <Link to={`/product/${item.product.slug}`} className="block truncate text-sm font-medium text-kelate-ink hover:text-kelate-wood">{item.product.name}</Link>
                      {item.variant && <span className="text-xs text-kelate-soft">Variant: {item.variant}</span>}
                      <div className="text-sm font-semibold text-kelate-darkwood">{fmtRM(item.product.price)}</div>
                    </div>
                    <div className="flex items-center rounded-sm border border-kelate-line">
                      <button onClick={() => updateCartQty(item.productId, item.variant, item.qty - 1)} className="p-1.5 text-kelate-soft" data-testid={`cart-minus-${item.product.slug}`}><Minus size={12} /></button>
                      <span className="w-8 text-center text-sm">{item.qty}</span>
                      <button onClick={() => updateCartQty(item.productId, item.variant, item.qty + 1)} className="p-1.5 text-kelate-soft" data-testid={`cart-plus-${item.product.slug}`}><Plus size={12} /></button>
                    </div>
                    <button onClick={() => removeCartItem(item.productId, item.variant)} className="p-2 text-kelate-soft hover:text-kelate-red" data-testid={`remove-${item.product.slug}`}><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <div className="lg:sticky lg:top-24 h-fit rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5" data-testid="cart-summary">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-kelate-soft">Summary</h2>
          <div className="mt-4">
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-kelate-ink"><Ticket size={12} className="text-kelate-wood" /> Voucher code</label>
            <div className="flex gap-2">
              <input
                data-testid="cart-voucher-input"
                value={voucher}
                onChange={(e) => setVoucher(e.target.value.toUpperCase())}
                placeholder="e.g. KELATE5"
                className="h-9 flex-1 rounded-sm border border-kelate-line bg-kelate-bg px-2.5 text-sm outline-none focus:border-kelate-wood"
              />
              <button
                data-testid="cart-voucher-apply"
                onClick={() => {
                  if (v) { localStorage.setItem('kelate_voucher', voucher); toast.success(`Voucher ${v.code} applied`, { description: vDesc }); }
                  else { localStorage.removeItem('kelate_voucher'); toast.error('Voucher not valid'); }
                }}
                className="rounded-sm border border-kelate-darkwood px-3 text-xs font-medium text-kelate-darkwood hover:bg-secondary"
              >
                Apply
              </button>
            </div>
            {v && <p className="mt-1.5 text-xs text-kelate-sage">{vDesc}</p>}
          </div>
          <div className="mt-4 space-y-2 border-t border-kelate-line pt-4 text-sm">
            <div className="flex justify-between text-kelate-soft"><span>Selected items</span><span data-testid="cart-items-count">{selectedItems.length}</span></div>
            <div className="flex justify-between text-kelate-soft"><span>Subtotal</span><span data-testid="cart-subtotal">{fmtRM(subtotal)}</span></div>
            <div className="flex justify-between text-kelate-soft"><span>Voucher</span><span>Applied at checkout</span></div>
            <div className="flex justify-between text-kelate-soft"><span>Delivery</span><span>Calculated at checkout</span></div>
            <div className="flex justify-between border-t border-kelate-line pt-2 text-base font-semibold text-kelate-darkwood"><span>Total</span><span data-testid="cart-total">{fmtRM(subtotal)}</span></div>
          </div>
          <p className="mt-2 text-[11px] text-kelate-soft">Pay on delivery only — cash, TNG QR or bank transfer when your order arrives.</p>
          <button
            data-testid="checkout-btn"
            disabled={selectedItems.length === 0}
            onClick={() => navigate('/checkout')}
            className="mt-4 w-full rounded-sm bg-kelate-darkwood py-3 text-sm font-medium text-kelate-surface hover:bg-kelate-wood disabled:opacity-40"
          >
            Checkout ({selectedItems.length})
          </button>
        </div>
      </div>
    </div>
  );
}
